// @rpg-unit/creature-bodies: animals in code from a few knobs (tier 2 recipe; IMP-0059, gap G13 of dry run 1). Three
// body plans, each a function from knobs a person would name ("taller", "longer in the leg", "floppy ears", "a curled
// tail") to an `@rpg/figure` card:
//   - `quadrupedCard`: four legs on @rpg/figure's four-legged motion (dogs, cats, goats, cows, sheep, foxes), shaped
//     after side-on walk-cycle comps: legs of three segments, nearly straight at rest (the fore elbow behind the
//     shoulder, the hind stifle ahead of the hip and the hock behind), and the gait's tuning comes from the body:
//     strides sized so no leg runs out of reach, the trot and gallop at the speeds an animal of its height changes gait
//     (Froude numbers 0.5 and 2.5), the step's height from the leg;
//   - `birdCard`: two legs on the biped motion (chickens, ducks, geese);
//   - `flyerCard`: no legs, hovering on the biped motion (bees, moths).
// The colours, sizes and features are the knobs; no species is built in (the presets unit holds those). Every card
// is inside @rpg/figure's ranges, so the game's content check passes it.
import type { FigureCard, JointSpec, MotionTuning, Piece, QuadTuning, Vec3 } from "@rpg/figure";

/** This unit's version (the manifest's and package.json's). */
export const CREATURE_BODIES_VERSION = "0.1.0" as const;

/** Planted defects beside what they break: the recipe's checks must go red with them on. */
export const CREATURE_FAULTS = {
  /** The strides come from the height alone, not the legs' reach: a short-legged animal over-reaches and crouches. */
  strideIgnoresReach: false,
  /** The legs are built straight: no rest bend, so the knees can bend either way and the legs can't reach. */
  legsStraight: false,
  /** The legs are built bent like stilts (the first build's): standing legs crouch, not straight as in the comps. */
  stiltLegs: false,
};

/** A four-legged animal's knobs. Lengths in metres; the rest are shares and choices. */
export interface QuadKnobs {
  /** Height at the shoulders (m). */
  height: number;
  /** Body length, chest to rump, as a multiple of the height (about 0.9 to 1.8; a dog's about 1.35). */
  length?: number;
  /** The legs' share of the height, ground to the chest's bottom (about 0.4 to 0.65; a dog's about 0.5). */
  legs?: number;
  /** How far the waist tucks up behind the chest: 0 a barrel (a cow's, a goat's), 1 a dog's (default: 1, less on a
   *  heavy animal). */
  tuck?: number;
  /** Stockiness: the body's width and the legs' thickness (0.6 slight to 1.6 heavy). */
  girth?: number;
  /** Neck length, as a share of the height (0 to 0.8). */
  neck?: number;
  /** How the neck is carried: 0 level (a cow's), 1 upright (default 0.4). */
  carriage?: number;
  /** The head's size (a share of the height, about 0.15 to 0.4) and its snout (0 flat-faced to 1 long). */
  head?: { size?: number; snout?: number };
  ears?: "up" | "floppy" | "small" | "none";
  horns?: "none" | "short" | "curved";
  tail?: "long" | "short" | "curl" | "tuft" | "bushy" | "none";
  feet?: "paws" | "hooves";
  /** Patches on the coat (a cow's), spots, or none. */
  pattern?: "plain" | "patches" | "spots";
  beard?: boolean;
  /** Whether it sits (a dog, a cat) and wags its tail. */
  sits?: boolean;
  wags?: boolean;
  /** How lively its gaits are: 1 as its size suggests, below 1 slower to trot and gallop, above 1 quicker. */
  energy?: number;
  /** Whether it gallops at all. */
  gallops?: boolean;
  colours: { coat: string; belly?: string; legs?: string; pattern?: string; nose?: string; eyes?: string; horns?: string; hooves?: string };
  seed?: number;
}

const G = 9.81;
const clamp = (x: number, a: number, b: number): number => Math.min(b, Math.max(a, x));
const r3 = (n: number): number => Math.round(n * 1000) / 1000;
const v3 = (x: number, y: number, z: number): Vec3 => [r3(x), r3(y), r3(z)];

/** A seeded stream (mulberry32) for patches and spots. */
function rng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A cylinder piece from `a` to `b` (model space) on a joint: its centre, its turn about x to the segment. */
function rod(joint: string, a: Vec3, b: Vec3, r0: number, r1: number, colour: string, mirror = false): Piece {
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  const len = Math.hypot(dy, dz, b[0] - a[0]);
  // a cylinder runs along y; turned about x by θ its axis is (0, cos θ, sin θ)
  const theta = Math.atan2(dz, dy);
  return { kind: "shape", joint, colour, shape: "cylinder", size: [r3(r0), r3(r1), r3(len)], segments: [6, 1], at: v3((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2), rot: [r3(theta), 0, 0], mirror };
}

/** A point on the segment from `a` to `b` at `t`, pushed off it (in the leg's y-z plane, toward +z for `dir` 1 or -z
 *  for -1) until the two segments through it are `ratio` times the straight line (bisection): a joint bent at rest. */
function bent(a: Vec3, b: Vec3, t: number, ratio: number, dir: 1 | -1): Vec3 {
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  const len = Math.hypot(dy, dz);
  // the perpendicular in the y-z plane, turned to point the way the joint bends
  let ny = -dz / len;
  let nz = dy / len;
  if (Math.sign(nz) !== dir) {
    ny = -ny;
    nz = -nz;
  }
  const at = (c: number): Vec3 => [a[0] + (b[0] - a[0]) * t, a[1] + dy * t + ny * c, a[2] + dz * t + nz * c];
  const total = (c: number): number => {
    const m = at(c);
    return Math.hypot(m[1] - a[1], m[2] - a[2]) + Math.hypot(b[1] - m[1], b[2] - m[2]);
  };
  let lo = 0;
  let hi = len;
  for (let i = 0; i < 40; i++) {
    const m = (lo + hi) / 2;
    if (total(m) < ratio * len) lo = m;
    else hi = m;
  }
  const p = at((lo + hi) / 2);
  return v3(p[0], p[1], p[2]);
}

/** An ellipsoid piece: a sphere of radius `ry` stretched to `rx` and `rz`. */
function blob(joint: string, at: Vec3, rx: number, ry: number, rz: number, colour: string, o: { rot?: Vec3; mirror?: boolean; segments?: [number, number] } = {}): Piece {
  return { kind: "shape", joint, colour, shape: "sphere", size: [r3(ry)], segments: o.segments ?? [8, 6], at, scale: v3(rx / ry, 1, rz / ry), ...(o.rot ? { rot: o.rot } : {}), ...(o.mirror ? { mirror: true } : {}) };
}

/**
 * A four-legged animal's card: joints, pieces and the four-legged motion's tuning, all from its knobs. The shape
 * follows side-on walk-cycle comps of a dog (ai-context/references/shared-links/2026-10-23-dog-walk-cycles): a deep
 * chest about half the height, a tucked loin, heavy haunches; legs of three segments, thick at the top and tapering:
 * fore (shoulder, an elbow behind it at the chest's bottom, a near-vertical forearm, a low wrist, a pastern to the
 * paw) and hind (hip, a stifle ahead of it, the shin back to a low hock, a near-vertical foot to the paw).
 */
export function quadrupedCard(k: QuadKnobs): FigureCard {
  const H = k.height;
  const legs = clamp(k.legs ?? 0.45, 0.3, 0.75);
  const L = H * clamp(k.length ?? 1.55, 0.7, 2.2);
  const g = clamp(k.girth ?? 1, 0.5, 2);
  const c = k.colours;
  const hooves = (k.feet ?? "paws") === "hooves";
  const cb = H * legs; // the chest's bottom: the legs' length below the body
  const D = H - cb; // the chest's depth
  const heavy = Math.min(1, Math.max(0, g - 1));
  const joints: JointSpec[] = [{ id: "body", at: v3(0, cb + D * 0.5, 0) }];
  const pieces: Piece[] = [];
  const legColour = c.legs ?? c.coat;
  // along the body: the chest's front at +L/2, the rump's back at -L/2

  // the torso: a deep chest, a waist tucked up toward the hind legs (fuller on a heavy animal), a rump; the back level
  const tuck = clamp(k.tuck ?? 1 - heavy, 0, 1);
  const waist = D * (0.6 + 0.38 * (1 - tuck));
  pieces.push(blob("body", v3(0, H - D * 0.5, L * 0.24), D * 0.33 * g, D * 0.5, L * 0.26, c.coat, { segments: [10, 7] }));
  pieces.push(blob("body", v3(0, H - waist * 0.5, -L * 0.04), D * 0.28 * g, waist * 0.5, L * 0.38, c.coat, { segments: [12, 7] }));
  // the back: one long shape over all three, so the top line reads as one curve
  pieces.push(blob("body", v3(0, H - D * 0.24, -L * 0.03), D * 0.27 * g, D * 0.24, L * 0.49, c.coat, { segments: [12, 6] }));
  pieces.push(blob("body", v3(0, H - D * 0.38, -L * 0.3), D * 0.31 * g, D * 0.38, L * 0.2, c.coat, { segments: [10, 7] }));
  // a lighter chest and underside (the comps' colour blocking)
  if (c.belly) {
    pieces.push(blob("body", v3(0, cb + D * 0.36, L * 0.33), D * 0.24 * g, D * 0.32, L * 0.13, c.belly));
  }
  const pattern = k.pattern ?? "plain";
  if (pattern !== "plain") {
    const rand = rng((k.seed ?? 7) * 31 + 5);
    const n = pattern === "patches" ? 9 : 12;
    const rx = D * 0.33 * g;
    const ry = (D + waist) * 0.25;
    const rz = L * 0.48;
    const cy = H - ry;
    for (let i = 0; i < n; i++) {
      // on the flanks (never along the back's top line): an angle round the side, a place along the body
      const a = (rand() < 0.5 ? -1 : 1) * (0.75 + rand() * 0.7);
      // spread along the body: each patch in its own share of the length
      const zz = ((i + 0.2 + rand() * 0.6) / n - 0.5) * rz * 1.4;
      const rr = Math.sqrt(Math.max(0, 1 - (zz / rz) ** 2));
      const px = Math.sin(a) * rx * rr;
      const py = cy + Math.cos(a) * ry * rr;
      const size = D * (pattern === "patches" ? 0.22 + rand() * 0.1 : 0.07 + rand() * 0.04);
      // sunk a little along the surface's normal, so a flat patch's rim stays on the curved coat
      const sink = size * 0.25;
      pieces.push({ kind: "shape", joint: "body", colour: c.pattern ?? "#222222", shape: "sphere", size: [r3(size)], segments: [7, 5], at: v3(px - Math.sin(a) * sink, py - Math.cos(a) * sink, zz), scale: [1, 0.3, 1.2], rot: [0, 0, r3(-a)] });
    }
  }

  // the legs (the left ones; mirrored to the right): rest bends at `ratio` of the straight line, which leaves the room a
  // stride needs while standing nearly straight, as in the comps
  const ratio = CREATURE_FAULTS.legsStraight ? 1.0 : CREATURE_FAULTS.stiltLegs ? 1.3 : 1.06;
  const x = -D * 0.2 * g;
  const pawR = H * (hooves ? 0.035 : 0.042) * g;
  const leg = {
    // fore: the leg swings from the shoulder blade's top, high in the chest, so it is one long pendulum to the wrist
    f: (() => {
      const top = v3(x, H - D * 0.22, L * 0.3);
      const ankle = v3(x, cb * (hooves ? 0.3 : 0.2), top[2] - H * 0.06);
      const toe = v3(x, 0, ankle[2] + cb * (hooves ? 0.12 : 0.2));
      return { top, knee: bent(top, ankle, 0.42, ratio + (hooves ? 0.02 : 0), -1), ankle, toe };
    })(),
    // hind: from the hip to a low hock behind it, the stifle forward
    h: (() => {
      const top = v3(x, H - D * 0.3, -L * 0.3);
      const ankle = v3(x, cb * (hooves ? 0.4 : 0.34), top[2] - H * 0.075);
      const toe = v3(x, 0, ankle[2] + cb * (hooves ? 0.04 : 0.08));
      return { top, knee: bent(top, ankle, 0.5, ratio + 0.035, 1), ankle, toe };
    })(),
  };
  for (const end of ["f", "h"] as const) {
    const { top, knee, ankle, toe } = leg[end];
    for (const side of ["l", "r"] as const) {
      const m = (p: Vec3): Vec3 => (side === "l" ? p : v3(-p[0], p[1], p[2]));
      joints.push(
        { id: `leg.${end}.${side}`, parent: "body", at: m(top) },
        { id: `shin.${end}.${side}`, parent: `leg.${end}.${side}`, at: m(knee) },
        { id: `foot.${end}.${side}`, parent: `shin.${end}.${side}`, at: m(ankle) },
        { id: `toe.${end}.${side}`, parent: `foot.${end}.${side}`, at: m(toe) },
      );
    }
    // the top: the upper arm's muscle down to the elbow, or the thigh's down to the stifle, thick as in the comps
    if (end === "f") {
      pieces.push(blob("leg.f.l", v3(x * 0.95, top[1] * 0.4 + knee[1] * 0.6, top[2] * 0.4 + knee[2] * 0.6 + H * 0.01), D * 0.12 * g, (top[1] - knee[1]) * 0.55, H * 0.075 * g, c.coat, { rot: [-0.3, 0, 0], mirror: true }));
      pieces.push(rod("leg.f.l", top, knee, H * 0.07 * g, H * 0.065 * g, c.coat, true));
    } else {
      // the haunch: from inside the rump, tapering down the thigh to the stifle
      pieces.push(blob("leg.h.l", v3(x * 0.95, top[1] * 0.62 + knee[1] * 0.38, top[2] * 0.62 + knee[2] * 0.38 - H * 0.015), D * 0.16 * g, (top[1] - knee[1]) * 0.62, H * 0.11 * g, c.coat, { rot: [0.25, 0, 0], mirror: true }));
      pieces.push(rod("leg.h.l", top, knee, H * 0.09 * g, H * 0.062 * g, c.coat, true));
    }
    pieces.push({ kind: "shape", joint: `shin.${end}.l`, colour: legColour, shape: "sphere", size: [r3(H * 0.058 * g)], segments: [6, 4], at: knee, mirror: true });
    pieces.push(rod(`shin.${end}.l`, knee, ankle, H * 0.062 * g, H * 0.04 * g, legColour, true));
    pieces.push({ kind: "shape", joint: `foot.${end}.l`, colour: legColour, shape: "sphere", size: [r3(H * 0.035 * g)], segments: [6, 4], at: ankle, mirror: true });
    // the foot: a pastern (fore) or the hock's long foot (hind) down to the paw or hoof
    const paw = v3(x, hooves ? H * 0.04 : pawR * 0.6, toe[2] - (hooves ? 0 : pawR * 0.6));
    pieces.push(rod(`foot.${end}.l`, ankle, paw, H * 0.037 * g, H * 0.034 * g, legColour, true));
    if (hooves) pieces.push({ kind: "shape", joint: `foot.${end}.l`, colour: c.hooves ?? "#3b2f2a", shape: "cylinder", size: [r3(H * 0.03 * g), r3(H * 0.038 * g), r3(H * 0.05)], segments: [6, 1], at: v3(x, H * 0.025, toe[2]), mirror: true });
    else pieces.push(blob(`foot.${end}.l`, v3(x, pawR * 0.5, toe[2] - pawR * 0.2), pawR, pawR * 0.55, pawR * 1.6, legColour, { mirror: true, segments: [6, 4] }));
  }

  // the neck and head: a thick neck carried forward from the chest's top, a wedge of a head at about the back's height
  const neckLen = H * clamp(k.neck ?? 0.3, 0, 0.9);
  const headSize = H * clamp(k.head?.size ?? 0.3, 0.1, 0.5);
  const snout = clamp(k.head?.snout ?? 0.5, 0, 1);
  const neckAt = v3(0, H - D * 0.3, L * 0.38);
  // the neck's carriage: 0 carries it level, 1 upright (default 0.35, about 32 degrees)
  const carry = (clamp(k.carriage ?? 0.35, 0, 1) * Math.PI) / 2;
  const headAt = v3(0, neckAt[1] + neckLen * Math.sin(carry), neckAt[2] + neckLen * Math.cos(carry));
  joints.push({ id: "neck", parent: "body", at: neckAt }, { id: "head", parent: "neck", at: headAt });
  if (neckLen > 0.02) pieces.push(rod("neck", v3(0, neckAt[1] - D * 0.1, neckAt[2] - D * 0.12), headAt, D * 0.3 * g, headSize * 0.4, c.coat));
  pieces.push(blob("head", v3(0, headAt[1] + headSize * 0.04, headAt[2] + headSize * 0.06), headSize * 0.36, headSize * 0.38, headSize * 0.45, c.coat, { segments: [8, 6] }));
  const snoutLen = headSize * (0.25 + snout * 0.75);
  const snoutFrom = v3(0, headAt[1] - headSize * 0.06, headAt[2] + headSize * 0.3);
  const snoutTo = v3(0, headAt[1] - headSize * 0.17, snoutFrom[2] + snoutLen);
  pieces.push(rod("head", snoutFrom, snoutTo, headSize * 0.3, headSize * 0.15, c.coat));
  pieces.push({ kind: "shape", joint: "head", colour: c.nose ?? "#2a1d1a", shape: "sphere", size: [r3(headSize * 0.08)], segments: [6, 4], at: v3(0, snoutTo[1] + headSize * 0.04, snoutTo[2]), scale: [1.3, 0.9, 0.8] });
  pieces.push({ kind: "shape", joint: "head", colour: c.eyes ?? "#1a1414", shape: "sphere", size: [r3(headSize * 0.06)], segments: [6, 4], at: v3(-headSize * 0.22, headAt[1] + headSize * 0.1, headAt[2] + headSize * 0.33), mirror: true });
  const ears = k.ears ?? "up";
  const earX = -headSize * 0.2;
  const earY = headAt[1] + headSize * 0.36;
  if (ears === "up") pieces.push({ kind: "shape", joint: "head", colour: c.coat, shape: "cone", size: [r3(headSize * 0.14), r3(headSize * 0.48)], segments: [4, 1], at: v3(earX, earY + headSize * 0.16, headAt[2] - headSize * 0.1), rot: [-0.2, 0, 0.2], mirror: true });
  else if (ears === "floppy") pieces.push({ kind: "shape", joint: "head", colour: c.legs ?? c.coat, shape: "box", size: [r3(headSize * 0.09), r3(headSize * 0.48), r3(headSize * 0.28)], at: v3(earX - headSize * 0.18, earY - headSize * 0.26, headAt[2] - headSize * 0.08), rot: [0, 0, -0.25], mirror: true });
  else if (ears === "small") pieces.push({ kind: "shape", joint: "head", colour: c.coat, shape: "sphere", size: [r3(headSize * 0.12)], segments: [5, 4], at: v3(earX - headSize * 0.18, earY - headSize * 0.04, headAt[2] - headSize * 0.1), scale: [1.6, 0.55, 1], mirror: true });
  const horns = k.horns ?? "none";
  if (horns === "short") pieces.push({ kind: "shape", joint: "head", colour: c.horns ?? "#e8dcc0", shape: "cone", size: [r3(headSize * 0.07), r3(headSize * 0.3)], segments: [5, 1], at: v3(-headSize * 0.16, earY + headSize * 0.1, headAt[2] - headSize * 0.02), rot: [-0.3, 0, 0.45], mirror: true });
  // curved horns sweep up and back over the neck (a goat's)
  else if (horns === "curved") {
    // two segments each side: up from the brow, then back over the neck
    const base = v3(-headSize * 0.13, earY + headSize * 0.05, headAt[2] + headSize * 0.02);
    const bend = v3(-headSize * 0.17, base[1] + headSize * 0.32, base[2] - headSize * 0.18);
    const tip = v3(-headSize * 0.2, bend[1] + headSize * 0.08, bend[2] - headSize * 0.42);
    pieces.push(rod("head", base, bend, headSize * 0.065, headSize * 0.05, c.horns ?? "#e8dcc0", true), rod("head", bend, tip, headSize * 0.05, headSize * 0.012, c.horns ?? "#e8dcc0", true));
  }
  if (k.beard) pieces.push({ kind: "shape", joint: "head", colour: c.legs ?? c.coat, shape: "cone", size: [r3(headSize * 0.09), r3(headSize * 0.32)], segments: [4, 1], flip: true, at: v3(0, snoutTo[1] - headSize * 0.3, snoutTo[2] - snoutLen * 0.35) });

  // the tail on its own joint (the motion wags it): from the croup, a long arc down, back and up at the tip
  const tailAt = v3(0, H - D * 0.12, -L * 0.48);
  joints.push({ id: "tail", parent: "body", at: tailAt });
  const tail = k.tail ?? "long";
  const tr = H * 0.034 * g;
  if (tail === "long") {
    const p1 = v3(0, tailAt[1] - H * 0.18, tailAt[2] - H * 0.14);
    const p2 = v3(0, p1[1] - H * 0.1, p1[2] - H * 0.2);
    const p3 = v3(0, p2[1] + H * 0.02, p2[2] - H * 0.2);
    const p4 = v3(0, p3[1] + H * 0.14, p3[2] - H * 0.06);
    pieces.push(rod("tail", tailAt, p1, tr * 1.2, tr * 0.95, c.coat), rod("tail", p1, p2, tr * 0.95, tr * 0.8, c.coat), rod("tail", p2, p3, tr * 0.8, tr * 0.65, c.coat), rod("tail", p3, p4, tr * 0.65, tr * 0.4, c.coat));
    // round the bends so the tail reads as one curve
    for (const [q, r] of [[p1, tr * 0.95], [p2, tr * 0.8], [p3, tr * 0.65]] as const) pieces.push({ kind: "shape", joint: "tail", colour: c.coat, shape: "sphere", size: [r3(r)], segments: [6, 4], at: q });
  } else if (tail === "bushy") pieces.push(blob("tail", v3(0, tailAt[1] - H * 0.1, tailAt[2] - H * 0.2), D * 0.14, D * 0.14, H * 0.24, c.coat, { rot: [0.5, 0, 0] }));
  else if (tail === "curl") pieces.push({ kind: "shape", joint: "tail", colour: c.coat, shape: "torus", size: [r3(D * 0.16), r3(tr), 4.5], segments: [8, 4], at: v3(0, tailAt[1] + D * 0.14, tailAt[2] - D * 0.06), rot: [0, 1.57, 0] });
  else if (tail === "tuft") {
    pieces.push(rod("tail", tailAt, v3(0, tailAt[1] - H * 0.42, tailAt[2] - H * 0.06), tr * 0.6, tr * 0.5, c.coat));
    pieces.push({ kind: "shape", joint: "tail", colour: c.pattern ?? c.legs ?? "#2a2422", shape: "sphere", size: [r3(tr * 1.5)], segments: [6, 4], at: v3(0, tailAt[1] - H * 0.45, tailAt[2] - H * 0.06), scale: [1, 1.6, 1] });
  } else if (tail === "short") pieces.push({ kind: "shape", joint: "tail", colour: c.coat, shape: "sphere", size: [r3(tr * 1.6)], segments: [6, 4], at: v3(0, tailAt[1] + tr * 0.4, tailAt[2] - tr * 0.6), scale: [1, 1.2, 1.4] });

  // the gait from the body: strides inside the legs' reach, gait changes at the animal's size's Froude numbers
  // how far a toe may stand from home: the three segments with the foot turned toward the hip, kept under the reach at
  // which the motion steps a foot early (97.5 %)
  const reachOf = (l: (typeof leg)["f"]): number => {
    const len = (a: Vec3, b: Vec3): number => Math.hypot(b[1] - a[1], b[2] - a[2]);
    const abc = 0.97 * (len(l.top, l.knee) + len(l.knee, l.ankle) + len(l.ankle, l.toe));
    // from the hip straight down, less the toe's rest offset ahead of or behind the hip (the room is on that side)
    return Math.sqrt(Math.max(0, abc * abc - l.top[1] ** 2)) - Math.abs(l.toe[2] - l.top[2]);
  };
  const maxOff = Math.min(reachOf(leg.f), reachOf(leg.h));
  const off = CREATURE_FAULTS.strideIgnoresReach ? H * 0.6 : maxOff * 0.85;
  const energy = clamp(k.energy ?? 1, 0.4, 2);
  const hipY = leg.h.top[1];
  const quad: QuadTuning = {
    walk: r3(clamp(0.04 * H, 0.01, 0.2)),
    trot: r3(clamp(Math.sqrt(0.5 * G * hipY) * energy, 0.05, 20)),
    stride: { walk: r3(clamp((2 * off) / 0.7, 0.05, 6)), trot: r3(clamp((2 * off) / 0.5, 0.05, 8)), gallop: r3(clamp((1.8 * off) / 0.38, 0.05, 12)) },
    stepHeight: r3(clamp(cb * 0.4, 0.005, 0.5)),
    bob: r3(clamp(H * 0.01, 0, 0.2)),
    pitchBob: 0.05,
    look: { yaw: 1.2, up: 0.5, down: 0.6, rate: 7 },
    breath: r3(clamp(H * 0.006, 0.001, 0.05)),
    eat: 2.2,
    lie: 0.45,
    peel: 0.5,
    fold: { fore: hooves ? 1.5 : 1.8, hind: 0.8 },
  };
  if (k.gallops !== false) quad.gallop = r3(clamp(Math.sqrt(2.5 * G * hipY) * energy, 0.1, 30));
  if (k.sits) quad.sit = 0.6;
  if (k.wags) quad.wag = { rate: 2.5, amp: 0.55 };
  return { seed: k.seed ?? 7, joints, pieces, quad };
}

/** A bird's knobs (two legs, the biped motion). */
export interface BirdKnobs {
  /** Height at the back (m). */
  height: number;
  /** Plumpness (0.6 to 1.6), the neck (0 to 1), the legs' share of the height (0.25 to 0.6). */
  girth?: number;
  neck?: number;
  legs?: number;
  comb?: boolean;
  /** A tail fan (a hen's), a short tail (a duck's) or none. */
  tail?: "fan" | "short" | "none";
  /** A beak's kind: pointed (a hen's) or flat (a duck's). */
  beak?: "pointed" | "flat";
  colours: { body: string; wing?: string; legs?: string; beak?: string; comb?: string; eyes?: string };
  seed?: number;
}

/** A bird's card: a body, a head on a neck, two legs, wings at its sides; the biped motion's tuning. */
export function birdCard(k: BirdKnobs): FigureCard {
  const H = k.height;
  const legs = clamp(k.legs ?? 0.4, 0.2, 0.7);
  const girth = clamp(k.girth ?? 1, 0.5, 2);
  const c = k.colours;
  const legY = H * legs;
  const r = (H - legY) * 0.5 * girth;
  const cy = legY + r * 0.85;
  const neck = clamp(k.neck ?? 0.4, 0, 1);
  const headAt = v3(0, cy + r * 0.8 + neck * H * 0.25, r * 0.85);
  const hs = H * 0.13;
  const joints: JointSpec[] = [
    { id: "body", at: v3(0, cy, 0) },
    { id: "head", parent: "body", at: headAt },
    { id: "leg.l", at: v3(-r * 0.35, legY, 0) },
    { id: "leg.r", at: v3(r * 0.35, legY, 0) },
  ];
  const pieces: Piece[] = [
    { kind: "shape", joint: "body", colour: c.body, shape: "sphere", size: [r3(r)], segments: [9, 7], at: v3(0, cy, 0), scale: [0.9, 0.95, 1.25] },
    { kind: "shape", joint: "body", colour: c.wing ?? c.body, shape: "sphere", size: [r3(r * 0.7)], segments: [7, 5], at: v3(-r * 0.75, cy + r * 0.05, -r * 0.1), scale: [0.3, 0.75, 1.15], mirror: true },
    rod("body", v3(0, cy + r * 0.5, r * 0.55), headAt, r * 0.35, hs * 0.55, c.body),
    { kind: "shape", joint: "head", colour: c.body, shape: "sphere", size: [r3(hs)], segments: [7, 5], at: headAt },
    { kind: "shape", joint: "head", colour: c.eyes ?? "#151010", shape: "sphere", size: [r3(hs * 0.16)], segments: [5, 4], at: v3(-hs * 0.6, headAt[1] + hs * 0.2, headAt[2] + hs * 0.45), mirror: true },
    { kind: "shape", joint: "leg.l", colour: c.legs ?? "#e0a83a", shape: "cylinder", size: [r3(H * 0.018), r3(H * 0.015), r3(legY)], segments: [5, 1], at: v3(-r * 0.35, legY / 2, 0), mirror: true },
    { kind: "shape", joint: "leg.l", colour: c.legs ?? "#e0a83a", shape: "box", size: [r3(H * 0.07), r3(H * 0.015), r3(H * 0.1)], at: v3(-r * 0.35, H * 0.008, H * 0.03), mirror: true },
  ];
  if ((k.beak ?? "pointed") === "pointed") pieces.push({ kind: "shape", joint: "head", colour: c.beak ?? "#e9b13a", shape: "cone", size: [r3(hs * 0.32), r3(hs * 0.8)], segments: [4, 1], at: v3(0, headAt[1] - hs * 0.1, headAt[2] + hs * 1.1), rot: [1.57, 0, 0] });
  else pieces.push({ kind: "shape", joint: "head", colour: c.beak ?? "#e9b13a", shape: "box", size: [r3(hs * 0.75), r3(hs * 0.2), r3(hs * 1)], at: v3(0, headAt[1] - hs * 0.2, headAt[2] + hs * 1.05) });
  if (k.comb) pieces.push({ kind: "shape", joint: "head", colour: c.comb ?? "#d6312b", shape: "box", size: [r3(hs * 0.25), r3(hs * 0.55), r3(hs * 0.9)], at: v3(0, headAt[1] + hs * 0.95, headAt[2]) });
  const tail = k.tail ?? "fan";
  if (tail === "fan") pieces.push({ kind: "shape", joint: "body", colour: c.wing ?? c.body, shape: "cone", size: [r3(r * 0.55), r3(r * 1.1)], segments: [5, 1], at: v3(0, cy + r * 0.55, -r * 1.05), rot: [-0.7, 0, 0], scale: [0.35, 1, 1] });
  else if (tail === "short") pieces.push({ kind: "shape", joint: "body", colour: c.wing ?? c.body, shape: "cone", size: [r3(r * 0.3), r3(r * 0.5)], segments: [4, 1], at: v3(0, cy + r * 0.2, -r * 1.2), rot: [-1.3, 0, 0] });
  const motion: MotionTuning = {
    walk: r3(0.04 * H),
    run: r3(Math.sqrt(0.6 * G * legY)),
    stepTime: [r3(clamp(0.6 * Math.sqrt(legY / G) * 2, 0.06, 1)), r3(clamp(0.35 * Math.sqrt(legY / G) * 2, 0.06, 1))],
    stepHeight: r3(clamp(legY * 0.3, 0, 0.4)),
    lead: 0.04,
    reach: 1.3,
    bob: r3(clamp(H * 0.03, 0, 0.15)),
    lean: { run: 0.3, glide: 0.3, jump: 0 },
    look: { yaw: 1.4, up: 0.6, down: 0.8, rate: 14 },
    breath: r3(clamp(H * 0.01, 0, 0.05)),
  };
  return { seed: k.seed ?? 11, joints, pieces, motion };
}

/** A flying insect's knobs (no legs; it hovers on the biped motion). */
export interface FlyerKnobs {
  /** Body length (m). */
  size: number;
  /** Hover height (m), and how far and fast it bobs. */
  hover: number;
  bob?: number;
  stripes?: number;
  colours: { body: string; stripe?: string; wings?: string; eyes?: string };
  seed?: number;
}

/** A flying insect's card: a striped body, wings, a head; the biped motion's hover. */
export function flyerCard(k: FlyerKnobs): FigureCard {
  const s = k.size;
  const c = k.colours;
  const y = 0;
  const joints: JointSpec[] = [
    { id: "body", at: v3(0, y, 0) },
    { id: "head", parent: "body", at: v3(0, y + s * 0.05, s * 0.42) },
  ];
  const pieces: Piece[] = [{ kind: "shape", joint: "body", colour: c.body, shape: "sphere", size: [r3(s * 0.3)], segments: [9, 7], at: v3(0, y, -s * 0.05), scale: [0.9, 0.9, 1.4] }];
  const n = Math.round(clamp(k.stripes ?? 2, 0, 4));
  for (let i = 0; i < n; i++) pieces.push({ kind: "shape", joint: "body", colour: c.stripe ?? "#1d1712", shape: "cylinder", size: [r3(s * 0.285), r3(s * 0.285), r3(s * 0.08)], segments: [10, 1], at: v3(0, y, -s * 0.05 - s * 0.12 * (i - (n - 1) / 2) * 1.6), rot: [1.5708, 0, 0] });
  pieces.push(
    { kind: "shape", joint: "head", colour: c.stripe ?? "#1d1712", shape: "sphere", size: [r3(s * 0.17)], segments: [7, 5], at: v3(0, y + s * 0.05, s * 0.42) },
    { kind: "shape", joint: "head", colour: c.eyes ?? "#0d0b0a", shape: "sphere", size: [r3(s * 0.07)], segments: [5, 4], at: v3(-s * 0.09, y + s * 0.09, s * 0.52), mirror: true },
    { kind: "shape", joint: "body", colour: c.wings ?? "#e8f2f7", glow: 0.25, shape: "sphere", size: [r3(s * 0.28)], segments: [7, 4], at: v3(-s * 0.26, y + s * 0.3, -s * 0.02), scale: [1.3, 0.1, 0.65], rot: [0, 0.4, 0.35], mirror: true },
  );
  const motion: MotionTuning = {
    walk: 0.05,
    run: 3,
    stepTime: [0.3, 0.2],
    stepHeight: 0,
    lead: 0,
    reach: 1,
    bob: 0,
    lean: { run: 0.2, glide: 0.2, jump: 0 },
    look: { yaw: 1.5, up: 0.8, down: 0.8, rate: 10 },
    breath: 0,
    hover: { height: r3(clamp(k.hover, 0, 3)), amp: r3(clamp(k.bob ?? s * 0.4, 0, 0.5)), rate: 3.2 },
  };
  return { seed: k.seed ?? 13, joints, pieces, motion };
}
